import {Record, List, Iterable} from 'immutable';
import {UserConditionType} from 'records/UserCondition';

export const CallConditionType = Object.freeze({
  MATCH_ANY: 'matchAny',
  MATCH_ALL: 'matchAll',
  USER: 'user',
  FIELD: 'field',
  AT_OPPORTUNITY_STAGE: 'at_opportunity_stage',
  FOR_OPPORTUNITY: 'for_opportunity',
  TO_ACCOUNT: 'to_account',
  WITH_CONTACT: 'with_contact',
  HAVING_OPPORTUNITY_AMOUNT: 'having_opportunity_amount',
  CONVERSATION_LENGTH: 'conversation_length',
  CALL_SCHEDULED: 'call_scheduled',
  TALK_LISTEN_RATIO: 'talk_listen_ratio',
  INTERCHANGES: 'interchanges',
  MENTIONED_AT_LEAST_ONCE: 'mentioned_at_least_once',
  MENTIONED_AT: 'mentioned',
  TOPIC_COVERAGE: 'topic_coverage',

  getCallCondition(details) {
    if (Iterable.isIterable(details)) {
      return details;
    }

    if (!details || !details.type) {
      return null;
    }

    // eslint-disable-next-line
    switch (details.type) {
      case CallConditionType.MATCH_ANY:
        return new CallConditionAny(details);
      case CallConditionType.MATCH_ALL:
        return new CallConditionAll(details);
      case CallConditionType.AT_OPPORTUNITY_STAGE:
        return new AtOpportunityStageCrmCondition(details);
      case CallConditionType.HAVING_OPPORTUNITY_AMOUNT:
        return new HavingOpportunityAmountCrmCondition(details);
      case CallConditionType.WITH_CONTACT:
        return new WithContactCrmCondition(details);
      case CallConditionType.TO_ACCOUNT:
        return new ToAccountCrmCondition(details);
      case CallConditionType.FOR_OPPORTUNITY:
        return new ForOpportunityCrmCondition(details);
      case CallConditionType.CONVERSATION_LENGTH:
        return new ConversationLengthCondition(details);
      case CallConditionType.CALL_SCHEDULED:
        return new CallScheduledCondition(details);
      case CallConditionType.TALK_LISTEN_RATIO:
        return new TalkListenRatioCondition(details);
      case CallConditionType.INTERCHANGES:
        return new InterChangesCondition(details);
      case CallConditionType.FIELD:
        return new FieldMatchingCondition(details);
      case CallConditionType.MENTIONED_AT_LEAST_ONCE:
        return new MentionedAtLeastOnceCondition(details);
      case CallConditionType.USER:
        return new CallConditionUser(details);
      case CallConditionType.MENTIONED_AT:
        return new MentionedAtCondition(details);
      case CallConditionType.TOPIC_COVERAGE:
        return new TopicCoverageCondition(details);
    }
  },

  isCrmCondition(type) {
    return [
      CallConditionType.AT_OPPORTUNITY_STAGE,
      CallConditionType.HAVING_OPPORTUNITY_AMOUNT,
      CallConditionType.WITH_CONTACT,
      CallConditionType.TO_ACCOUNT,
      CallConditionType.FOR_OPPORTUNITY,
    ].includes(type);
  },

  isFieldCondition(type) {
    return type === CallConditionType.FIELD;
  },

  isConversationCondition(type) {
    return [
      CallConditionType.CONVERSATION_LENGTH,
      CallConditionType.CALL_SCHEDULED,
      CallConditionType.TALK_LISTEN_RATIO,
      CallConditionType.INTERCHANGES,
    ].includes(type);
  },

  isKeywordCondition(type) {
    return [CallConditionType.MENTIONED_AT, CallConditionType.MENTIONED_AT_LEAST_ONCE].includes(
      type
    );
  },

  isTopicCoverageCondition(type) {
    return [CallConditionType.TOPIC_COVERAGE].includes(type);
  },
});

export class CallConditionAny extends Record({
  type: CallConditionType.MATCH_ANY,
  matchAny: List(),
}) {
  constructor(props = {matchAny: List()}) {
    const topMatchAny = Iterable.isIterable(props.matchAny) ? props.matchAny : List(props.matchAny);
    const matchAny = topMatchAny.map(c => CallConditionType.getCallCondition(c));
    super({
      ...props,
      matchAny,
    });
  }

  ready() {
    return this.matchAny.size > 0 && this.matchAny.every(context => context.ready());
  }
}

export class CallConditionAll extends Record({
  type: CallConditionType.MATCH_ALL,
  matchAll: List(),
}) {
  constructor(props = {matchAll: List()}) {
    const topMatchAll = Iterable.isIterable(props.matchAll) ? props.matchAll : List(props.matchAll);
    const matchAll = topMatchAll.map(c => CallConditionType.getCallCondition(c));
    super({
      ...props,
      matchAll,
    });
  }

  ready() {
    return this.matchAll.size > 0 && this.matchAll.every(context => context.ready());
  }
}

export class CallConditionUser extends Record({
  type: CallConditionType.USER,
  userCondition: null,
}) {
  constructor(props) {
    super({
      ...props,
      userCondition: UserConditionType.ensureUserCondition(props.userCondition),
    });
  }

  ready() {
    return !!this.userCondition;
  }
}

export class AtOpportunityStageCrmCondition extends Record({
  type: CallConditionType.AT_OPPORTUNITY_STAGE,
  crmStageId: null,
}) {
  constructor(props) {
    super({
      ...props,
      crmStageId: props.crmStageId || props.value,
    });
  }

  ready() {
    return !!this.getValue();
  }

  getValue() {
    return this.crmStageId;
  }
}

export class ForOpportunityCrmCondition extends Record({
  type: CallConditionType.FOR_OPPORTUNITY,
  crmEngagementId: null,
}) {
  constructor(props) {
    super({
      ...props,
      crmEngagementId: props.crmEngagementId || props.value,
    });
  }

  ready() {
    return !!this.getValue();
  }

  getValue() {
    return this.crmEngagementId;
  }
}

export class ToAccountCrmCondition extends Record({
  type: CallConditionType.TO_ACCOUNT,
  crmAccountId: null,
}) {
  constructor(props) {
    super({
      ...props,
      crmAccountId: props.crmAccountId || props.value,
    });
  }

  ready() {
    return !!this.getValue();
  }

  getValue() {
    return this.crmAccountId;
  }
}

export class WithContactCrmCondition extends Record({
  type: CallConditionType.WITH_CONTACT,
  crmContactId: null,
}) {
  constructor(props) {
    super({
      ...props,
      crmContactId: props.crmContactId || props.value,
    });
  }

  ready() {
    return !!this.getValue();
  }

  getValue() {
    return this.crmContactId;
  }
}

export class HavingOpportunityAmountCrmCondition extends Record({
  type: CallConditionType.HAVING_OPPORTUNITY_AMOUNT,
  dollarRange: {start: 0, end: 0},
}) {
  constructor(props) {
    super({
      ...props,
      dollarRange: props.dollarRange || props.value,
    });
  }

  ready() {
    return this.dollarRange && this.dollarRange.end >= this.dollarRange.start;
  }

  getValue() {
    return this.dollarRange;
  }
}

export class FieldMatchingCondition extends Record({
  type: CallConditionType.FIELD,
  searchFieldId: null,
  enumValue: null,
}) {
  ready() {
    return !!this.enumValue && !!this.searchFieldId;
  }
}

export class ConversationLengthCondition extends Record({
  type: CallConditionType.CONVERSATION_LENGTH,
  timeRange: {start: 0, end: 0},
}) {
  constructor(props) {
    super({
      ...props,
      timeRange: props.timeRange || props.value,
    });
  }

  ready() {
    return this.timeRange && this.timeRange.end >= this.timeRange.start;
  }

  getValue() {
    return this.timeRange;
  }
}

export class CallScheduledCondition extends Record({
  type: CallConditionType.CALL_SCHEDULED,
  scheduled: true,
}) {
  constructor(props) {
    const scheduled = props.scheduled == null ? props.value : props.scheduled;
    super({
      ...props,
      scheduled: scheduled,
    });
  }
  ready() {
    return true;
  }

  getValue() {
    return this.scheduled;
  }
}

export class TalkListenRatioCondition extends Record({
  type: CallConditionType.TALK_LISTEN_RATIO,
  percentRange: {start: 0, end: 0},
}) {
  constructor(props) {
    super({
      ...props,
      percentRange: props.percentRange || props.value,
    });
  }

  ready() {
    return this.percentRange && this.percentRange.end >= this.percentRange.start;
  }

  getValue() {
    return this.percentRange;
  }
}

export class InterChangesCondition extends Record({
  type: CallConditionType.INTERCHANGES,
  exchangeRange: {start: 0, end: 0},
}) {
  constructor(props) {
    super({
      ...props,
      exchangeRange: props.exchangeRange || props.value,
    });
  }

  ready() {
    return this.exchangeRange && this.exchangeRange.end >= this.exchangeRange.start;
  }

  getValue() {
    return this.exchangeRange;
  }
}

export const SpeakerCondition = Object.freeze({
  CALLER: 'CALLER',
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'PROSPECT',
});

export const KeywordTopicPhrase = Record({
  filterWordId: undefined,
  keyword: undefined,
});

export class MentionedAtLeastOnceCondition extends Record({
  type: CallConditionType.MENTIONED_AT_LEAST_ONCE,
  speakerType: undefined,
  keywordTopicPhrase: undefined,
  negated: false,
}) {
  constructor(props = {}) {
    super({
      ...props,
      keywordTopicPhrase: props.keywordTopicPhrase
        ? new KeywordTopicPhrase(props.keywordTopicPhrase)
        : undefined,
    });
  }
  ready() {
    return (
      !!this.keywordTopicPhrase &&
      !!(this.keywordTopicPhrase.filterWordId || this.keywordTopicPhrase.keyword)
    );
  }
}

export class MentionedAtCondition extends Record({
  type: CallConditionType.MENTIONED_AT,
  speakerType: undefined,
  keywordTopicPhrase: undefined,
  range: {start: 0, end: 20},
  negated: false,
}) {
  constructor(props = {}) {
    super({
      ...props,
      keywordTopicPhrase: props.keywordTopicPhrase
        ? new KeywordTopicPhrase(props.keywordTopicPhrase)
        : undefined,
    });
  }

  ready() {
    return (
      !!this.keywordTopicPhrase &&
      !!(this.keywordTopicPhrase.filterWordId || this.keywordTopicPhrase.keyword) &&
      !!this.range
    );
  }
}

export class TopicCoverageCondition extends Record({
  type: CallConditionType.TOPIC_COVERAGE,
  speakerType: undefined,
  keywordTopicPhrase: undefined,
  range: {start: 0, end: 20},
  negated: false,
}) {
  ready() {
    return (
      !!this.keywordTopicPhrase &&
      !!(this.keywordTopicPhrase.filterWordId || this.keywordTopicPhrase.keyword) &&
      !!this.range
    );
  }
}

export default class CallTrigger extends Record({
  type: 'call_trigger',
  conditions: null,

  getUserIds() {
    return List();
  },
}) {
  constructor(props = {conditions: null}) {
    super({
      ...props,
      conditions: CallConditionType.getCallCondition(props.conditions),
    });
  }
}
